import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Power, X } from "lucide-react";
import useServerTable from "../hooks/useServerTable";
import usePerm from "../hooks/usePerm";
import { useApi } from "../hooks/useApi";
import materialSetApi from "../api/materialSetApi";
import { materialApi } from "../api/apiIndex";
import PageHeader from "../components/PageHeader";
import Card from "../components/Card";
import Btn from "../components/Btn";
import Input from "../components/Input";
import Modal from "../components/Modal";
import Select2 from "../components/Select2";
import T from "../theme/colors";

const th = {
  padding: "11px 14px", textAlign: "left", fontSize: 11, fontWeight: 800,
  textTransform: "uppercase", letterSpacing: "0.03em", color: T.text2, whiteSpace: "nowrap"
};
const td = { padding: "11px 14px", fontSize: 13, color: T.text0, verticalAlign: "top" };
const money = v => `\u20B9${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

/**
 * Material Set master.
 *
 * A set is picked directly on a purchase order - nobody groups lines by hand.
 * The price here is LOCKED for the PO; each member's list rate is the weight
 * the set price is split by, so the same set always reaches stock at the same
 * per-material figures.
 */
export default function MaterialSetPage() {
  const can = usePerm("MATERIAL_SET");
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);

  const t = useServerTable(materialSetApi.paged, {
    defaultSortBy: "setName", defaultSortDir: "asc", defaultSize: 25,
    extraFilters: { q: search.trim() || undefined },
  });
  const rows = t.items || [];

  const doToggle = async r => {
    try { await materialSetApi.toggle(r.id); t.reload(); }
    catch (e) { alert(e?.response?.data?.message || "Could not change the status."); }
  };
  const doDelete = async r => {
    if (!window.confirm(`Delete set "${r.setName}"?\n\nOrders that already used it keep their frozen prices.`)) return;
    try { await materialSetApi.delete(r.id); t.reload(); }
    catch (e) { alert(e?.response?.data?.message || "Could not delete this set."); }
  };

  return (
    <div>
      <PageHeader title="Material Sets" subtitle="Materials bought together, priced as one"
        breadcrumb={["Masters", "Material Sets"]}
        actions={can.create && <Btn variant="primary" icon={<Plus size={14} />} onClick={() => setModal({ mode: "create" })}>New Set</Btn>} />

      <div style={{ marginTop: 12 }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search set or material…"
          style={{
            width: 380, padding: "8px 12px", borderRadius: 7, border: `1px solid ${T.border}`,
            background: T.bgCard, color: T.text0, fontSize: 13, outline: "none", boxSizing: "border-box"
          }} />
      </div>

      <Card style={{ marginTop: 12 }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: T.bgInput }}>
                <th style={th}>Set</th>
                <th style={th}>Members</th>
                <th style={{ ...th, textAlign: "right" }}>Sum of list</th>
                <th style={{ ...th, textAlign: "right" }}>Set price</th>
                <th style={{ ...th, textAlign: "center" }}>Status</th>
                <th style={{ ...th, textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {t.loading && <tr><td colSpan={6} style={{ ...td, textAlign: "center", color: T.text2, padding: 30 }}>Loading…</td></tr>}
              {!t.loading && rows.length === 0 && (
                <tr><td colSpan={6} style={{ ...td, textAlign: "center", color: T.text2, padding: 40 }}>
                  {search.trim() ? "No sets match this search." : "No material sets yet."}
                </td></tr>
              )}
              {!t.loading && rows.map(r => {
                const listSum = (r.members || []).reduce((s, m) => s + (Number(m.listRate) || 0), 0);
                return (
                  <tr key={r.id} style={{ borderTop: `1px solid ${T.border}` }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.setName}</div>
                      {r.description && <div style={{ fontSize: 11, color: T.text2 }}>{r.description}</div>}
                    </td>
                    <td style={td}>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {(r.members || []).map(m => (
                          <span key={m.id} style={{
                            fontSize: 11, fontWeight: 600, padding: "2px 8px",
                            borderRadius: 4, background: T.purpleBg, color: T.purple
                          }}>
                            {m.materialName}{m.materialVariantName ? ` \u00b7 ${m.materialVariantName}` : ""} @ {Number(m.listRate).toFixed(2)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td style={{ ...td, textAlign: "right", color: T.text2 }}>{money(listSum)}</td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{money(r.setRate)}</td>
                    <td style={{ ...td, textAlign: "center" }}>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 4,
                        background: r.isActive ? T.successBg : T.bgInput, color: r.isActive ? T.success : T.text2
                      }}>
                        {r.isActive ? "ACTIVE" : "INACTIVE"}
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: "center", whiteSpace: "nowrap" }}>
                      {can.edit && <button onClick={() => setModal({ mode: "edit", row: r })} title="Edit" style={iconBtn(T.warn)}><Pencil size={14} /></button>}
                      {can.edit && <button onClick={() => doToggle(r)} title={r.isActive ? "Deactivate" : "Activate"} style={iconBtn(T.info)}><Power size={14} /></button>}
                      {can.del && <button onClick={() => doDelete(r)} title="Delete" style={iconBtn(T.danger)}><Trash2 size={14} /></button>}
                      {!can.edit && !can.del && <span style={{ color: T.text2 }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {modal && <SetModal mode={modal.mode} row={modal.row} onClose={() => setModal(null)}
        onSaved={() => { setModal(null); t.reload(); }} />}
    </div>
  );
}

const iconBtn = color => ({ border: "none", background: "none", cursor: "pointer", color, padding: "2px 5px", verticalAlign: "middle" });

function SetModal({ mode, row, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const { data: matData = [] } = useApi(materialApi.getAll);
  const materials = Array.isArray(matData) ? matData : [];
  const [setName, setSetName] = useState(row?.setName || "");
  const [setRate, setSetRate] = useState(row?.setRate != null ? String(row.setRate) : "");
  const [description, setDescription] = useState(row?.description || "");
  const [members, setMembers] = useState(row?.members?.length
    ? row.members.map(m => ({ ...m, materialId: String(m.materialId), listRate: String(m.listRate) }))
    : [{ materialId: "", listRate: "" }, { materialId: "", listRate: "" }]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const setMem = (i, patch) => setMembers(p => p.map((m, k) => (k === i ? { ...m, ...patch } : m)));
  const listSum = members.reduce((s, m) => s + (Number(m.listRate) || 0), 0);
  const rate = Number(setRate) || 0;

  const save = async () => {
    const clean = members.filter(m => m.materialId).map(m => ({
      id: m.id, materialId: Number(m.materialId), listRate: Number(m.listRate) || 0,
      materialVariantId: m.materialVariantId || undefined,
    }));
    if (!setName.trim()) { setErr("Set name is required"); return; }
    if (!(rate >= 0)) { setErr("Set price is required"); return; }
    if (clean.length < 2) { setErr("A set needs at least two materials"); return; }
    if (clean.some(m => !(m.listRate > 0))) { setErr("Every member needs a list rate above zero"); return; }
    setBusy(true); setErr("");
    try {
      const body = { setName: setName.trim(), setRate: rate, description: description.trim() || undefined, members: clean };
      if (isEdit) await materialSetApi.update(row.id, body); else await materialSetApi.create(body);
      onSaved();
    } catch (e) { setErr(e?.response?.data?.message || "Could not save this set."); }
    setBusy(false);
  };

  return (
    <Modal open onClose={onClose} width={640} title={isEdit ? `Edit ${row.setName}` : "New Material Set"}>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
        <Input label="Set Name *" value={setName} onChange={setSetName} placeholder="Collar & Sleeve Rib" />
        <Input label="Price per set ₹ *" type="number" value={setRate} onChange={setSetRate} placeholder="22.00" />
      </div>
      <Input label="Description" value={description} onChange={setDescription} placeholder="Optional" />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.03em", color: T.text2 }}>Members</span>
        <Btn size="sm" variant="outline" icon={<Plus size={12} />}
          onClick={() => setMembers(p => [...p, { materialId: "", listRate: "" }])}>Add Material</Btn>
      </div>
      <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
        {members.map((m, i) => {
          const share = listSum > 0 && rate > 0 ? rate * (Number(m.listRate) || 0) / listSum : 0;
          return (
            <div key={i} style={{
              display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto", gap: 8, alignItems: "center",
              padding: "8px 10px", borderTop: i === 0 ? "none" : `1px solid ${T.border}`
            }}>
              <Select2 value={m.materialId} onChange={v => setMem(i, { materialId: v })} placeholder="Material"
                options={materials.map(x => ({ value: String(x.id), label: x.materialName }))} />
              <input type="number" value={m.listRate} placeholder="List rate"
                onChange={e => setMem(i, { listRate: e.target.value })} style={rowInput} />
              {/* Live preview of the split, so the operator sees what each
                  member will cost in stock before saving. */}
              <span style={{ fontSize: 12, color: T.text2, textAlign: "right" }}>
                share {share ? money(share) : "\u2014"}
              </span>
              <button type="button" onClick={() => setMembers(p => p.filter((_, k) => k !== i))}
                style={{ border: "none", background: "none", cursor: "pointer", color: T.danger }}><X size={15} /></button>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 12, color: T.text2, marginTop: 8 }}>
        Sum of list rates {money(listSum)} → set price {money(rate)}
        {listSum > 0 && rate > 0 && ` (${(rate / listSum * 100).toFixed(1)}% of list)`}
      </div>

      {err && <div style={{ color: T.danger, fontSize: 13, marginTop: 10 }}>{err}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
        <Btn variant="secondary" onClick={onClose} disabled={busy}>Cancel</Btn>
        <Btn onClick={save} loading={busy}>{isEdit ? "Save Changes" : "Create Set"}</Btn>
      </div>
    </Modal>
  );
}

const rowInput = {
  padding: "6px 9px", borderRadius: 6, fontSize: 13, width: "100%",
  border: `1px solid ${T.border}`, background: T.bgCard, color: T.text0, outline: "none", boxSizing: "border-box"
};